import { createContext, useContext, useCallback, useEffect, useMemo, type ReactNode, type ReactElement, type CSSProperties } from 'react'
import { useIsDesktop } from './useIsDesktop'
import { onSessionEnd, sessionSignal } from './sessionEnded'
import { isDroppingLateAnswer } from '@papan/api-client'
import { createPortal } from 'react-dom'
import { Toaster, ToastBar, CheckmarkIcon, ErrorIcon, toast as hotToast, type Toast as HotToast } from 'react-hot-toast'
import { X, CheckCircle, AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

/** A single button beside the message — "Undo", "View". Dismisses the toast
 *  when pressed. */
export interface ToastAction {
  label: string
  onClick: () => void
}

export interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
  action?: ToastAction
}

export interface ProgressToast {
  id: string
  message: string
  current: number
  /** `null` or `<= 0` = INDETERMINATE: the total is unknown (or only a lower
   *  bound), so no percentage and no "n / total" is shown — never a figure that
   *  can overshoot. */
  total: number | null
  status: 'running' | 'completed' | 'error'
  completedMessage?: string
  errorMessage?: string
}

export interface ProgressController {
  /**
   * Update progress (current out of total). Pass `total` when the size of the
   * work only becomes known after the toast was opened — the toast then turns
   * from indeterminate into a real bar. Without it, a caller that learns its
   * total later can only write that total into the MESSAGE, and the bar stays
   * at 0% while the label counts up (which is what upload's "Creating folders
   * (40/120)…" did).
   */
  update: (current: number, message?: string, total?: number) => void
  /** Mark as completed with success message */
  complete: (message?: string) => void
  /** Mark as failed with error message */
  fail: (message: string) => void
  /** Get the toast ID */
  id: string
}

/** `id`: a STABLE id — showing a toast under an id already on screen replaces it instead of stacking a second
 *  one (a notice a held key would otherwise repeat, e.g. "an undo is still running"). Omitted: a fresh id. */
export interface ToastOptions { id?: string }

interface ToastContextValue {
  addToast: (type: ToastType, message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => string
  removeToast: (id: string) => void
  success: (message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => string
  error: (message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => string
  warning: (message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => string
  info: (message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => string
  /**
   * Start a progress toast. Returns controller to update/complete it.
   *
   * THE SIZE IS NOT KNOWN — the bar sweeps instead of filling and no percentage
   * is shown — when `total` is `null` (unknown, or only a lower bound, e.g. a
   * truncated count) or `<= 0` (work that reports no intermediate progress — a
   * folder delete is one request that commits at the end — rather than passing
   * 1 and leaving the bar parked at 0% until it jumps to done).
   */
  progress: (message: string, total: number | null) => ProgressController
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

/**
 * Null-safe variant: returns `null` outside a `ToastProvider` instead of
 * throwing (mirrors `useRealtimeOptional`). For leaf components that MAY render
 * outside the app's global provider — e.g. in isolated unit tests, a preview,
 * or a different shell — and only want a toast when one is available.
 */
export function useToastOptional(): ToastContextValue | null {
  return useContext(ToastContext)
}

const DEFAULT_DURATION = 4000
const COMPLETED_DURATION = 2000

/**
 * How long a toast stays up: the default, or DOUBLE it for a long message.
 *
 * A flat 4s is right for "Saved" and dismisses a 45-word error about a third of
 * the way through, so it does scale — but only to 8s, and never further. Long
 * messages are not held until read (that would be ~14s for the error that
 * prompted this, and permanent for anything past it); HOVER is the escape
 * hatch, and it preserves remaining time, so a reader who needs longer takes it.
 *
 * 200 wpm is the usual figure for on-screen prose; the extra second covers
 * noticing the toast before starting to read. The result is clamped to
 * [DEFAULT, 2 × DEFAULT] so the ramp is smooth rather than a cliff between two
 * fixed values.
 *
 * It takes the LARGER of a word estimate and a character estimate, because word
 * count alone is backwards for unspaced content: a URL, a base64 blob or a
 * one-line stack trace is a single "word", so a 20,000-character message scored
 * as 1 word and got the SHORTEST possible duration.
 *
 * 1,200 chars/min is 200 wpm at ~6 characters per word including the space, so
 * the two agree for ordinary prose and the character leg only takes over where
 * word count has stopped describing the content. A lower figure makes chars
 * dominate everywhere and the word estimate becomes dead code.
 *
 * The trade-off, stated because it is a real one: on TOUCH there is no hover,
 * so a phone reader gets 8s and no way to extend it. If long messages turn out
 * to matter on mobile, the fix is a tap-to-hold on the toast, not a longer
 * timer for everyone.
 */
const READ_WORDS_PER_MINUTE = 200
const READ_CHARS_PER_MINUTE = 1_200
const NOTICE_MS = 1_000
const MAX_DURATION = DEFAULT_DURATION * 2

export function readingDuration(message: string): number {
  const text = message.trim()
  if (text.length === 0) return DEFAULT_DURATION
  const words = text.split(/\s+/).filter(Boolean).length
  const byWords = (words / (READ_WORDS_PER_MINUTE / 60)) * 1_000
  const byChars = (text.length / (READ_CHARS_PER_MINUTE / 60)) * 1_000
  const ms = Math.max(byWords, byChars) + NOTICE_MS
  return Math.min(MAX_DURATION, Math.max(DEFAULT_DURATION, Math.round(ms)))
}

/**
 * Deliberately unhurried — a toast that snaps in reads as a glitch rather than
 * as something arriving. Still well inside react-hot-toast's own ~1s
 * dismiss→remove delay, so an exit always finishes before the node is pulled.
 */
const ENTER_MS = 420
const EXIT_MS = 320

/**
 * ONE motion for both shells: rise + scale up in, sink + scale down out.
 *
 * The stack is bottom-anchored on desktop (bottom-right) and on phone
 * (bottom-centre) alike, so the same vertical motion is correct for both and
 * the per-shell split that used to live here is gone — only the POSITION still
 * varies by breakpoint.
 *
 * The easing differs by direction on purpose: entry overshoots slightly so the
 * toast settles into place, exit does not, because a toast on its way out
 * should not draw the eye back.
 */
const ENTER_ANIMATION = `toast-enter-rise ${ENTER_MS}ms cubic-bezier(.21,1.02,.73,1) forwards`
const EXIT_ANIMATION = `toast-exit-sink ${EXIT_MS}ms cubic-bezier(.4,0,1,1) forwards`

/**
 * Toasts run on `react-hot-toast`, which owns the queue, the dismiss timers,
 * the pause/resume clock AND the visual shell.
 *
 * Simple toasts render through its own `ToastBar`, so the pill shape, padding,
 * shadow and 350px cap are the library's, not ours. The enter/exit MOTION is
 * NOT — it is overridden on the bar's `style` (see `ENTER_ANIMATION`), because
 * the library's default travels vertically by 150% and this stack wanted a
 * short rise-and-scale. Two things are added through the seams it provides
 * rather than by replacing it:
 *  - **the dismiss ✕** via `ToastBar`'s `children({ icon, message })` render
 *    prop, which keeps its shell;
 *  - **the action button** inside the MESSAGE node, which matters for more than
 *    layout: `ToastBar` spreads `ariaProps` onto the message wrapper, so an
 *    action rendered there sits INSIDE `role="alert"`. `taskbar-folders.spec.ts`
 *    does `getByRole('alert').…getByRole('button', { name: 'Undo' })`, which
 *    only resolves if the button is a descendant of the alert.
 *
 * Type is carried by a COLOURED ICON on one neutral dark surface, rather than by
 * four coloured surfaces.
 *
 * Two behaviours are inherited rather than reimplemented, and both were
 * hand-rolled here before:
 *  - **Hover pauses the whole stack.** Its container binds
 *    `onMouseEnter={startPause} / onMouseLeave={endPause}`, and it preserves
 *    REMAINING time (`pauseDuration` accumulates; the timer is
 *    `(duration + pauseDuration) - elapsed`). That is what the old
 *    `remainingRef` did by hand.
 *  - **The enter/exit LIFECYCLE** via `t.visible` — the signal that a toast is
 *    leaving, so it can animate out instead of vanishing mid-frame. Only the
 *    signal is inherited; the motion it drives is ours, per above.
 */

/**
 * One implementation of the message box, so a long message can never again grow
 * the toast to viewport width. See `SURFACE`'s comment.
 *
 * NOT clamped. A clamp puts the "…" exactly where an error's actionable half
 * usually is — "…because the destination is read-only for your role" — so it
 * hides the sentence the user needed. Height is cheap; ambiguity is not.
 *
 * `max-h-[50vh]` + scroll bounds the pathological case (a server dumping a
 * stack trace) without losing a character: it can never cover the viewport, and
 * unlike an ellipsis the remainder stays reachable, including by touch. Real
 * copy never comes close to it.
 */
const MESSAGE_CLASS = 'text-sm text-zinc-100 min-w-0 break-words max-h-[50vh] overflow-y-auto'

/** The one neutral surface, shared by EVERY toast — simple and progress alike.
 *
 *  `SURFACE_COLORS` is the shared part. It has to be an inline style object
 *  because react-hot-toast takes `style`, not a class. That made it tempting to leave the progress toast on its
 *  Tailwind `bg-zinc-900 border-zinc-700`, and the two promptly drifted: these
 *  hexes are Tailwind v3's palette and this repo is on v4 (oklch), so the
 *  borders already resolved to `63,63,70` and `63,63,71` — one step apart, in
 *  the same stack. Imperceptible, and exactly the kind of gap that widens on
 *  the next palette change. One object, both consumers.
 *
 *  `react-hot-toast`'s default is dark-text-on-white,
 *  which reads as foreign against the drive and chat. Everything else about the
 *  bar — radius, padding, shadow, the 350px cap — is its own.
 *
 *  It must NOT carry `animation`: ToastBar merges `{...defaultAnimation,
 *  ...barStyleProp, ...toast.style}` and this object is the LAST of the three,
 *  so an animation here would win over the horizontal one set on the bar. */
/** Named so the icon sweep can REFERENCE it instead of repeating the literal —
 *  see `TYPE_ICON`. A surface change that left a stale `secondary` behind would
 *  make the checkmark's stroke invisible, silently. */
const SURFACE_BG = '#18181b' // zinc-900

const SURFACE_COLORS: CSSProperties = {
  background: SURFACE_BG,
  color: '#f4f4f5', // zinc-100
  border: '1px solid #3f3f46', // zinc-700
}

/**
 * The simple toasts' bar: the shared colours PLUS the layout its own ROW needs.
 *
 * The split is not cosmetic. These two properties are row-specific, and
 * spreading them onto the progress toast — a `flex-col` — BREAKS it:
 * `align-items: flex-start` stops its children stretching, which collapsed the
 * bar row from ~330px to 32px and the track itself to ZERO width, and the `gap`
 * silently overrode its `gap-2`. That shipped for one commit under a comment
 * claiming both were "inert on this column layout". They are not.
 */
const SURFACE: CSSProperties = {
  ...SURFACE_COLORS,
  // Aligned to the TOP so a wrapped multi-line message keeps its icon beside
  // the FIRST line rather than drifting to the vertical middle of the block.
  alignItems: 'flex-start',
  // Replaces the horizontal half of the message wrapper's `margin: 4px 10px`,
  // which index.css zeroes so the three children share one top edge.
  gap: '10px',
}

/**
 * The ONE icon palette, for all four types and for the progress toast.
 *
 * It has to be hex rather than Tailwind classes because the library's own
 * icons take colour PROPS, not classNames — and while `warning`/`info` could
 * have kept `text-amber-400`/`text-blue-400`, that left these two entries dead
 * and the same four colours defined twice, in two systems (v3 hex here, v4
 * oklch there). That is the drift `SURFACE_COLORS` was just unified to avoid.
 */
const ICON_COLOR = {
  success: '#4ade80', // green-400
  error: '#f87171', // red-400
  warning: '#fbbf24', // amber-400
  info: '#60a5fa', // blue-400
} as const

/**
 * The icon sits in a box exactly one LINE tall and centres within it, so it
 * lines up with the first line of text at any message length. Without this the
 * bar's `flex-start` pins the glyph to the padding edge and it reads high.
 */
function IconSlot({ children }: { children: ReactNode }) {
  return <span className="flex items-center justify-center h-5 w-[20px] shrink-0">{children}</span>
}

/**
 * `success` and `error` use react-hot-toast's OWN icons, which draw themselves
 * in (circle scale + stroke sweep) — that animation is a signature part of the
 * library's feel and is the reason to take its icons rather than draw our own.
 * They are themed with our palette via `primary`/`secondary` rather than
 * restyled. `secondary` is the stroke that sweeps ACROSS the disc, so it must
 * be the SURFACE colour, not a lighter tint of the primary.
 *
 * The library ships no warning or info glyph, so those two keep the lucide
 * icons and get a matching pop-in, rather than being left as the only two that
 * appear without motion.
 */
const TYPE_ICON: Record<ToastType, ReactElement> = {
  success: (
    <IconSlot>
      <CheckmarkIcon primary={ICON_COLOR.success} secondary={SURFACE_BG} />
    </IconSlot>
  ),
  error: (
    <IconSlot>
      <ErrorIcon primary={ICON_COLOR.error} secondary={SURFACE_BG} />
    </IconSlot>
  ),
  warning: (
    <IconSlot>
      <AlertTriangle size={18} style={{ color: ICON_COLOR.warning }} className="shrink-0 animate-toast-icon-pop" />
    </IconSlot>
  ),
  info: (
    <IconSlot>
      <Info size={18} style={{ color: ICON_COLOR.info }} className="shrink-0 animate-toast-icon-pop" />
    </IconSlot>
  ),
}

/** The message node handed to `react-hot-toast`. The action lives HERE, not
 *  beside the bar, so it falls inside the `role="alert"` wrapper — see the
 *  file header. */
function ToastMessage({ id, message, action }: { id: string; message: string; action?: ToastAction }) {
  return (
    <span className="flex items-start gap-3 min-w-0">
      {/* WIDTH IS CAPPED BY THE LIBRARY (350px) AND THE MESSAGE WRAPS. The
          pre-migration item was `w-[320px] truncate` at rest and
          `w-fit min-w-[320px]` with no max-width on hover, so a long message
          grew to whatever the text wanted: measured in a real browser at
          1920px, a 274-char message rendered a 1841px single line (96% of the
          viewport) and a 355-char one reached `left: 0`. A long message must
          get TALLER, never wider. `min-w-0` is what actually lets this flex
          child shrink enough to wrap. */}
      <span className={MESSAGE_CLASS}>{message}</span>
      {action && (
        <button
          type="button"
          onClick={() => {
            action.onClick()
            hotToast.dismiss(id)
          }}
          className="text-sm font-semibold text-white/90 hover:text-white underline-offset-2 hover:underline shrink-0"
        >
          {action.label}
        </button>
      )}
    </span>
  )
}

function ProgressToastItem({ t, state }: { t: HotToast; state: ProgressToast }) {
  // Indeterminate when the size of the work is not known: `null` (unknown or
  // only a lower bound) or `<= 0` (see `progress`'s docblock).
  // Safety net for a total that turns out LOW (e.g. a lower-bound count): the
  // bar never passes 100% and the counter never reads "600 / 55".
  const total = state.total
  const indeterminate = total === null || total <= 0
  const current = total === null || total <= 0 ? state.current : Math.min(state.current, total)
  const percentage = indeterminate || total === null ? 0 : Math.min(100, Math.round((current / total) * 100))

  const icon =
    state.status === 'error' ? (
      <AlertCircle size={18} style={{ color: ICON_COLOR.error }} className="shrink-0" />
    ) : state.status === 'completed' ? (
      <CheckCircle size={18} style={{ color: ICON_COLOR.success }} className="shrink-0" />
    ) : (
      <Loader2 size={18} style={{ color: ICON_COLOR.info }} className="shrink-0 animate-spin" />
    )

  const displayMessage = state.status === 'error' ? state.errorMessage : state.status === 'completed' ? state.completedMessage || state.message : state.message

  return (
    <div
      className="flex flex-col gap-2 px-2.5 py-2 rounded-lg border shadow-lg w-[350px] max-w-[calc(100vw-2rem)]"
      // The SAME motion as every other toast. A progress toast renders through
      // `toast.custom` and so bypasses ToastBar, which is where the simple
      // toasts get theirs — without this it keeps whatever it was given last
      // and animates differently from the row above it in the same stack.
      // COLOURS only, never `SURFACE` — see its comment. Taking the shared
      // object means this can never drift from the simple toasts' palette;
      // taking its row layout would break this column.
      style={{ ...SURFACE_COLORS, animation: t.visible ? ENTER_ANIMATION : EXIT_ANIMATION }}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        {icon}
        {/* Same wrap rule as `ToastMessage` — a long label grows down. */}
        <p className={`${MESSAGE_CLASS} flex-1`}>{displayMessage}</p>
        {state.status !== 'running' && (
          <button onClick={() => hotToast.dismiss(t.id)} className="text-zinc-400 hover:text-white transition-colors shrink-0" aria-label="Dismiss">
            <X size={16} />
          </button>
        )}
      </div>

      {state.status === 'running' && (
        <div className="flex items-center gap-2" data-testid={indeterminate ? 'progress-indeterminate' : undefined}>
          <div
            className="flex-1 h-1.5 bg-zinc-700 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={indeterminate ? undefined : 100}
            aria-valuenow={indeterminate ? undefined : percentage}
          >
            {/* Fixed blue, no status ternary: this whole block renders only
                while `running`, so an error/completed colour would be
                unreachable here — which it was, for as long as it existed. The
                ICON above is what varies by status. */}
            {indeterminate ? (
              // No percentage exists, so none is drawn: a segment sweeps the
              // track instead. Keyframes and the reduced-motion fallback live
              // beside the toast's other animations in index.css.
              <div className="h-full w-2/5 bg-blue-500 rounded-full animate-toast-progress-indeterminate" />
            ) : (
              <div className="h-full bg-blue-500 transition-all duration-300 ease-out" style={{ width: `${percentage}%` }} />
            )}
          </div>
          {!indeterminate && <span className="text-xs text-zinc-400 min-w-[3ch] text-right">{percentage}%</span>}
          {/* Indeterminate: no percentage, but a count of what is done so far. */}
          {indeterminate && current > 0 && <span className="text-xs text-zinc-400 text-right">{current}</span>}
        </div>
      )}

      {state.status === 'running' && !indeterminate && total !== null && total > 1 && (
        <p className="text-xs text-zinc-500">
          {current} / {total}
        </p>
      )}
    </div>
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  // #1316: a session end (sign-out, another account, a tenant / identity switch, leaving the share view) clears every
  // toast on screen — the next person on a shared computer never reads the previous session's "Renamed Payroll…".
  // (A late answer raising a NEW toast is dropped at the request — api-client's dropIfSessionEnded.) Subscribed in an
  // effect: StrictMode's double mount subscribes, unsubscribes and subscribes again — one listener, no mounted flag.
  // EVERY session end, not only an account change: P10 forces the clear at an account change, and one rule for every
  // end (P2, P15) over a reason table — a toast is a notice, not data (decision D4 in the #1316 PR).
  useEffect(() => onSessionEnd(() => { hotToast.remove() }), [])

  const addToast = useCallback((type: ToastType, message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => {
    const id = opts?.id ?? `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
    // #1316: a toast raised while a dropped late answer is handled (its caller's catch) belongs to the session that
    // ended — shown to whoever is here now it could name the previous account's file. Nothing is shown.
    if (isDroppingLateAnswer()) return id
    return hotToast(
      // A FUNCTION, so the id is in scope for the action's own dismiss.
      () => <ToastMessage id={id} message={message} action={action} />,
      {
        id,
        // An explicit duration from the caller always wins; otherwise it is
        // derived from the message, between the default and double it.
        duration: duration ?? readingDuration(message),
        icon: TYPE_ICON[type],
        style: SURFACE,
        // `role="alert"` on EVERY type, matching the pre-migration markup.
        // react-hot-toast defaults to `role="status"`, which would silently
        // break `getByRole('alert')` in `bin-permissions.spec.ts` and
        // `taskbar-folders.spec.ts` — the latter matches a SUCCESS toast, so
        // narrowing "alert" to errors only is not safe either.
        //
        // That leaves a real tension, recorded so nobody "fixes" it blindly:
        // `role="alert"` carries an IMPLICIT `aria-live="assertive"`, and a
        // success toast has no business interrupting a screen-reader user. The
        // explicit `polite` below overrides it — which ARIA permits, though
        // support is uneven. The semantically clean split (status for
        // success/info, alert for error/warning) is not free: it would break
        // the two specs above, one of them on another feature's branch. Change
        // the specs FIRST if this is ever revisited.
        ariaProps: {
          role: 'alert',
          'aria-live': type === 'error' || type === 'warning' ? 'assertive' : 'polite',
        },
      },
    )
  }, [])

  const removeToast = useCallback((id: string) => {
    hotToast.dismiss(id)
  }, [])

  const success = useCallback((message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => addToast('success', message, duration, action, opts), [addToast])
  const error = useCallback((message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => addToast('error', message, duration, action, opts), [addToast])
  const warning = useCallback((message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => addToast('warning', message, duration, action, opts), [addToast])
  const info = useCallback((message: string, duration?: number, action?: ToastAction, opts?: ToastOptions) => addToast('info', message, duration, action, opts), [addToast])

  const progress = useCallback((message: string, total: number | null): ProgressController => {
    const id = `progress-${Date.now()}-${Math.random().toString(36).slice(2)}`
    // The toast's own state lives here, not in React state: re-issuing
    // `toast.custom` with the SAME id replaces its renderer, which is how
    // react-hot-toast models an updating toast. Keeping a parallel copy in a
    // `useState` would be a second source of truth for the same row.
    let state: ProgressToast = {
      id,
      message,
      current: 0,
      total,
      status: 'running',
    }
    const render = () =>
      hotToast.custom(
        t => <ProgressToastItem t={t} state={state} />,
        // `Infinity` because a progress toast is dismissed by its controller
        // reaching a terminal state, never by the clock.
        { id, duration: Infinity },
      )
    if (!isDroppingLateAnswer()) render()

    // Once a terminal state has been reached the toast is on its way out, and
    // `render()` is an UPSERT — so a late `update()` re-adds a row that was
    // already dismissed, seconds after the user watched it go.
    //
    // Note what this is NOT: `update()` spreads the existing state, so the
    // revived row keeps `status: 'completed'` and therefore still renders a ✕.
    // An earlier version of this comment claimed it came back as `running`
    // (`duration: Infinity`, no ✕) and was therefore unremovable — that was
    // wrong, and the test written from it passed with the guard deleted.
    // The bug is a resurrected toast, not a stuck one.
    // #1316: a controller from a session that has ended changes nothing — its upsert would put the cleared row back;
    // one created while a dropped late answer is handled shows nothing at all
    const session = sessionSignal()
    let settled = isDroppingLateAnswer()

    return {
      id,
      update: (current: number, newMessage?: string, newTotal?: number) => {
        if (settled || session.aborted) return
        state = {
          ...state,
          current,
          message: newMessage ?? state.message,
          total: newTotal ?? state.total,
        }
        render()
      },
      complete: (completedMessage?: string) => {
        if (settled || session.aborted) return
        settled = true
        state = {
          ...state,
          status: 'completed',
          current: state.total ?? state.current,
          completedMessage,
        }
        render()
        setTimeout(() => hotToast.dismiss(id), COMPLETED_DURATION)
      },
      fail: (errorMessage: string) => {
        if (settled || session.aborted) return
        settled = true
        state = { ...state, status: 'error', errorMessage }
        render()
        setTimeout(() => hotToast.dismiss(id), DEFAULT_DURATION)
      },
    }
  }, [])

  const value = useMemo<ToastContextValue>(
    () => ({ addToast, removeToast, success, error, warning, info, progress }),
    [addToast, removeToast, success, error, warning, info, progress],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  )
}

/**
 * The portaled toast stack, split out of `ToastProvider` on purpose: it reads
 * the viewport breakpoint, and `ToastProvider` wraps the ENTIRE app — so
 * calling the hook up there would re-render every screen each time someone
 * crosses 768px (a window drag, a device-emulation toggle). Here the re-render
 * is one stack.
 */
function ToastViewport() {
  const isDesktop = useIsDesktop()

  if (typeof document === 'undefined') return null

  return createPortal(
    // Portaled to <body>, a SIBLING of #root — not rendered inline in the
    // provider tree inside #root. Two reasons, both real:
    //  - A full-screen overlay that marks #root `inert` (the identity
    //    shell's accessibility sweep, and Modal's) would otherwise disable
    //    the toast and drop it from the a11y tree even though z-[10000]
    //    keeps it VISUALLY on top — the feedback shows but cannot be read
    //    or dismissed. Outside #root, the sweep never reaches it.
    //  - `position: fixed` is viewport-relative only until an ancestor
    //    gains a `transform`/`filter`/`perspective`, which re-anchors it;
    //    being a direct body child keeps it immune to any such ancestor.
    // React context still flows (portals preserve the React tree, not the
    // DOM one).
    //
    // The attribute goes on THIS wrapper rather than on react-hot-toast's
    // own container, which we do not render and cannot add attributes to:
    // a full-screen overlay that sweeps `document.body` marking siblings
    // `inert` must SKIP it (see `IdentityShell`'s sweep).
    <div data-overlay-root="">
      <Toaster
        position={isDesktop ? 'bottom-right' : 'bottom-center'}
        gutter={8}
        containerStyle={{
          // Above EVERY other layer — toasts are feedback and must never be
          // buried under the surface that triggered them. react-hot-toast
          // defaults its container to 9999, which is exactly the agent
          // modals' z-index; this must stay ahead of the highest anywhere
          // in the app. Do not lower it to "just above" some particular
          // dialog; the next surface to raise its own stacking wins again.
          zIndex: 10000,
          // Sits above any persistent surface (e.g. UploadDock) that
          // publishes its height via --upload-dock-offset. Falls back to
          // 1rem when nothing is below.
          bottom: 'calc(1rem + var(--upload-dock-offset, 0px))',
          // Symmetric insets so `bottom-center` actually centres on a phone
          // while `bottom-right` still hugs the right edge on desktop. The
          // library caps the bar at 350px, and a flex child cannot exceed
          // its containing block, so a narrow phone shrinks it rather than
          // overflowing — no extra max-width needed here.
          left: '1rem',
          right: '1rem',
        }}
        // No `toastOptions` defaults: every toast this provider mints
        // passes its own `duration` and `style`, so a default here is dead
        // for simple toasts and IGNORED for progress ones (they render
        // through `toast.custom`, which never reads `t.style`). Leaving
        // them in reads as configuration that is doing something.
      >
        {t =>
          // `toast.custom` (the progress toasts) bypasses ToastBar entirely
          // and renders its own node, so it must not be wrapped in this
          // shell — it would get a second background and padding.
          t.type === 'custom' ? (
            <>{typeof t.message === 'function' ? t.message(t) : t.message}</>
          ) : (
            <ToastBar
              toast={t}
              // The library's default is vertical (`translate3d(0, ±150%)`),
              // which reads as dropping in from above on a bottom-right
              // stack. Set on the BAR's style rather than the toast's:
              // ToastBar merges default → this → toast.style, so this beats
              // the default while `SURFACE` stays colour-only.
              style={{
                animation: t.visible ? ENTER_ANIMATION : EXIT_ANIMATION,
              }}
            >
              {({ icon, message }) => (
                <>
                  {icon}
                  {message}
                  {/* Same one-line-tall slot as the icon: a bare 16px
                          glyph top-aligns inside a 20px line box and reads 2px
                          high even once the wrapper's margin is gone. */}
                  <button
                    onClick={() => hotToast.dismiss(t.id)}
                    className="flex items-center justify-center h-5 text-zinc-400 hover:text-white transition-colors shrink-0"
                    aria-label="Dismiss"
                  >
                    <X size={16} />
                  </button>
                </>
              )}
            </ToastBar>
          )
        }
      </Toaster>
    </div>,
    document.body,
  )
}
